import { useState, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Box, TextLinkButton, IconChevronDown, IconChevronUp } from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import {
  Accordion as BaseAccordion,
  AccordionHeader as BaseAccordionHeader,
  AccordionContent as BaseAccordionContent
} from '@/components/accordion'
import { colors, breakpoints, fontSizes, fontWeights } from '@/styles'
import { toUnit } from '@/utils/styles'
import cornerShape from './corner.svg'
import Context from '../accordion/context'
import ArrowUp from '../icons/arrow-up.svg'
import ArrowDown from '../icons/arrow-down.svg'

export const Table = styled.table`
  width: 100%;
  border-top: none;
  border-collapse: separate;
  border-radius: 6px;
  table-layout: fixed;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    border-bottom: 1px solid ${colors.mediumLightGrey};
    border-radius: 0;
  }
`

export const TableHeader = styled.thead``

const HeaderCell = styled.th`
  border-bottom: 2px solid var(--border-color-neutral-light, #d2d7df);
  border-top: 1px solid var(--border-color-neutral-light, #d2d7df);
  background: var(--background-neutral-light, #f7f8fb);

  &:first-child {
    border-left: 1px solid var(--border-color-neutral-light, #d2d7df);
    border-radius: 6px 0 0 0;
    // can't use overflow hidden with sticky elements, believe me I tried :-|
    // so using SVGs to hide the overflow at top left/right corners instead
    :before {
      content: url(${cornerShape});
      position: absolute;
      top: -8px;
      left: -5px;
      width: 12px;
      height: 19px;
      transform: scaleX(0.5) scaleY(0.5) rotate(270deg);
    }
  }

  &:last-child {
    border-right: 1px solid var(--border-color-neutral-light, #d2d7df);
    border-radius: 0 6px 0 0;
    :after {
      content: url(${cornerShape});
      position: absolute;
      top: -8px;
      right: -5px;
      width: 12px;
      height: 19px;
      transform: scaleX(0.5) scaleY(0.5);
    }
  }

  ${props =>
    props.width &&
    `
    width: ${props.width};
  `}
`

const SortableCell = styled(HeaderCell)`
  vertical-align: middle;

  ${props =>
    props.onClick &&
    `
    cursor: pointer;
  `}

  ${props =>
    props.active &&
    `

    p:after {
      content: url(${props.ascending ? ArrowUp : ArrowDown});
      display: block;
      margin-left: 5px;

      ${
        props.alpha &&
        `
      content: url(${props.ascending ? ArrowUp : ArrowDown});
        `
      }
    }
  `}
`

export const TableHeaderCell = props => <SortableCell {...props}>{props.children}</SortableCell>

export const TableBody = styled.tbody``

export const TableRow = styled.tr`
  ${props =>
    props.sticky &&
    `
    th {
      position: sticky;
      top: ${props.top || 0}px;
      z-index: 20;
    }
  `}
`

export const TableHeaderText = styled.p`
  color: ${colors.backgroundNeutral};
  font-size: 14px;
  font-weight: ${fontWeights.bold};
  letter-spacing: 0.25px;
  line-height: 20px;
  text-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.09);
  height: 48px;
  padding: 12px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: ${props => (props.alignLeft ? 'flex-start' : 'center')};
  text-align: ${props => props.alignLeft && 'left'};

  ${props => props.clickable && `cursor: pointer`}

  ${props =>
    props.firstCell &&
    `
    justify-content: flex-start;
  `}

  ${props =>
    props.lastCell &&
    `
    justify-content: flex-end;
    margin-right: 30px;
    cursor: pointer;

    :active {
      opacity: 0.7;
    }
  `}
`

export const TableCell = styled.td`
  @media only screen and (min-width: ${breakpoints.ipadMiniMax}) {
    &:first-child {
      border-left: 1px solid var(--border-color-neutral-light, #d2d7df);
    }
    &:last-child {
      border-right: 1px solid var(--border-color-neutral-light, #d2d7df);
    }
  }

  // This border is a fallback for Safari which doesn't properly position the
  // :after element in AccordionTableRow due to it being display: table-row;
  // This border doesn't look quite as good because the bottom border overrides
  // the left border causing a small gap in the blue line between rows
  ${props =>
    props.blueBorder &&
    `
      @media only screen and (min-width: ${breakpoints.tabletLandscape}) {
        &:first-child {
          border-left: 3px solid ${vars.borderColor.info};
        }
      }
    `}

  ${props =>
    props.width &&
    `
    width: ${props.width};
  `}
  ${props =>
    props.align &&
    `
    text-align: ${props.align};
  `}
`

export const OptionsWrapper = styled.div`
  display: flex;
  justify-content: ${props => props.justifyContent ?? 'flex-end'};
  align-items: center;
  padding-right: ${props => props.paddingRight ?? '18px'};
`

export const StyledTableCell = styled(TableCell)`
  display: table-cell;
  vertical-align: ${props => props.verticalAlign ?? 'middle'};
  position: relative;
  border-bottom: 1px solid ${colors.mediumLightGrey};
  padding-top: ${props => props.paddingTop ?? '0px'};
  > a {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: ${props => props.justifyContent ?? 'center'};
    align-items: center;
  }

  ${props =>
    props.rounded &&
    `
    @media only screen and (min-width: ${breakpoints.tabletLandscape}) {
      border-radius: 6px;
      overflow: hidden;
    }
  `}

  ${props =>
    props.hideAt &&
    `
    @media only screen and (max-width: ${props.hideAt}) {
      display: none;
    }
  `}

  ${props =>
    props.maxWidth &&
    `
    max-width: ${props.maxWidth};
  `}
`

export const SingleTableRow = styled(TableRow)`
  cursor: pointer;
  border-bottom: 1px solid ${colors.grey};
  background-color: ${colors.white};
  padding: 5px;

  &:nth-child(even) {
    background: #f3f5f7;
  }
  &:nth-child(odd) {
    background: #ffffff;
  }

  &:last-child {
    td {
      &:first-child {
        border-radius: 0 0 0 6px;
      }
      &:last-child {
        border-radius: 0 0 6px 0;
      }
    }
  }
`

export const AccordionTableRow = styled(TableRow)`
  cursor: pointer;
  border-bottom: 1px solid ${colors.grey};
  background-color: #eaeff3;
  padding: 5px;
  position: relative;

  &:nth-child(even) {
    background: #f3f5f7;
  }
  &:nth-child(odd) {
    background: #ffffff;
  }

  @media screen and (min-width: ${breakpoints.desktopSmall}) {
    &:after {
      content: '';
      position: absolute;
      border: 2px solid ${vars.borderColor.info};
      top: 0;
      left: 0;
      height: calc(100% - 3px);
    }
  }
`

const AccordionHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background: var(--background-form-accent-soft-active, #f0f6fc);
  padding: 12px;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    align-items: flex-start;
    padding: 20px 16px;
  }

  @media screen and (max-width: ${breakpoints.smallPhone}) {
    padding: 20px 15px;
  }
`

export const AccordionHeaderText = styled.p`
  display: flex;
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  letter-spacing: -0.39px;
  line-height: 22px;
  margin: 0;

  span {
    display: block;
    font-weight: ${fontWeights.medium};
    margin-right: 30px;
    min-width: 175px;
  }

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    display: block;

    span {
      margin-right: 20px;
      margin-bottom: 3px;
      min-width: 120px;
    }
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 260px;
  }

  @media screen and (max-width: ${breakpoints.smallPhone}) {
    max-width: 220px;
  }
`

const AccordionHeaderRow = styled(TableRow)`
  background: var(--background-form-accent-soft-active, #f0f6fc);
`

export const Accordion = BaseAccordion

const AccordionButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`

const ChevronWrapper = styled.div`
  svg {
    color: ${vars.foregroundColor.link};
    width: 12px;
    height: 12px;
    margin-left: 12px;
  }
`

export const AccordionHeader = ({ children, colSpan, listingsView, unitCount, isMobile }) => {
  const { t } = useTranslation()
  const value = useContext(Context)
  const [isActive, setIsActive] = useState(value.active)
  const handleClick = () => {
    setIsActive(!isActive)
  }

  const buttonText = isActive
    ? t('l.listing.list.collapse_units.button', {
        defaultValue: 'Collapse {{unitCount}} Units',
        unitCount
      })
    : t('l.listing.list.expand_units.button', {
        defaultValue: 'Expand {{unitCount}} Units',
        unitCount
      })

  useEffect(() => {
    setIsActive(value.active)
  }, [value.active])
  return (
    <BaseAccordionHeader>
      <AccordionHeaderRow>
        <StyledTableCell colSpan={colSpan}>
          <AccordionHeaderContent>
            <AccordionButtonContainer listingsView={listingsView}>
              {children}
              <TextLinkButton onClick={handleClick}>
                <Box display="flex" alignItems="center">
                  {!isMobile && buttonText}
                  <ChevronWrapper>
                    {isActive ? <IconChevronDown /> : <IconChevronUp />}
                  </ChevronWrapper>
                </Box>
              </TextLinkButton>
            </AccordionButtonContainer>
          </AccordionHeaderContent>
        </StyledTableCell>
      </AccordionHeaderRow>
    </BaseAccordionHeader>
  )
}

export const AccordionContent = BaseAccordionContent

export const MobileRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  width: 100%;
  margin-left: ${props => (props.indent ? 40 : (props.marginLeft ?? 16))}px;

  @media screen and (max-width: ${breakpoints.smallPhone}) {
    margin-left: ${props => (props.indent ? 40 : 15)}px;
  }
`

export const MobileUnitText = styled.p`
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.39px;
  line-height: 22px;
  margin: 0;
  ${props =>
    props.justifyContent &&
    `
    display: flex;
    justify-content: center;
  `}

  strong {
    font-weight: ${fontWeights.medium};
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    text-overflow: ellipsis;
    max-width: 260px;
  }

  @media screen and (max-width: ${breakpoints.smallPhone}) {
    max-width: 220px;
  }

  ${props =>
    props.link &&
    `
    color: ${colors.link};
    font-weight: ${fontWeights.regular};
    cursor: pointer;
  `}
`

export const MobileUnitDetails = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${props => props.top || 12}px;
  margin-bottom: ${props => toUnit(props.bottom) || 0};

  p {
    color: ${colors.regular};
    font-size: 14px;
    letter-spacing: -0.34px;
    line-height: 16px;
    margin: 0;

    &:not(:first-child) {
      padding-left: 8px;
      margin-left: 8px;
      border-left: 1px solid ${colors.regular};
    }
  }
`

export const CellText = styled.div`
  display: block;
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  letter-spacing: -0.39px;
  line-height: 22px;
  padding: 0;
  margin: ${props => (props.margin ? props.margin : '0 15px')};
  text-align: ${props => props.textAlign ?? 'center'};
  padding-top: ${props => props.top || 12}px;
  padding-bottom: ${props => props.bottom || 12}px;
  padding-right: ${props => props.paddingRight || 0};

  ${props =>
    props.width &&
    `
    width: ${props.width};
  `}

  strong {
    font-weight: ${fontWeights.medium};
  }

  ${props =>
    props.firstCell &&
    `
    padding: 20px 0;
    text-align: left;
    margin: 0;
    margin-left: 12px;
  `}

  ${props =>
    props.lastCell &&
    `
    color: ${colors.link};
    text-align: right;
    margin-right: 30px;

    @media screen and (max-width: ${breakpoints.tabletLandscape}) {
      padding: 0;
      margin-right: 25px;
      margin-bottom: ${props.bottom || 25}px;
    }

    @media screen and (max-width: ${breakpoints.smallPhone}) {
      margin-right: 15px;
    }
`}

${props =>
    props.indent &&
    `
    margin-left: 50px;
  `}

${props =>
    props.link &&
    `
    color: ${colors.link};
    cursor: pointer;
  `}
`
